import React, { useState, useCallback, useEffect } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import UserSearch from './UserSearch'
import UserProfile from './UserProfile'
import AcademySection from './AcademySection'
import CustomAttributes from './CustomAttributes'
import PlatformUserIDs from './PlatformUserIDs'
import SessionMTools from './SessionMTools'
import OffersSection from './OffersSection'
import PurchaseHistory from './PurchaseHistory'
import Reservations from './Reservations'
import HelpTickets from './HelpTickets'
import ChallengeData from './ChallengeData'
import EmailComms from './EmailComms'
import TabContainer from './TabContainer'
import CreateProfileForm, { CreateProfileFormData } from './CreateProfileForm'
import CustomerMerge from './CustomerMerge'
import MTMerge from './MTMerge'
import UserMenu from './auth/UserMenu'
import AuditLog from './AuditLog'
import ChangeLog from './ChangeLog'
import GiftCardAuditLog from './GiftCardAuditLog'
import AdminActivityLog from './AdminActivityLog'
import UserManagement from './UserManagement'
import StudioChallengesButton from './StudioChallengesButton'
import BulkTagsButton from './BulkTagsButton'
import FWB from './FWB'
import FSAReceipts from './FSAReceipts'
import BulkPoints from './BulkPoints'
import { useAuth } from './auth/AuthProvider'
import { getUserBySessionMId, createUser } from '../services/api'
import { mtMergeReleaseEnabled } from '../config/features'
import {
  MT_MERGE_OPERATION_STORAGE_KEY,
  mtMergeOperationStatusMessage,
} from './mtMergeState'

const AppContainer = styled.div`
  min-height: 100vh;
  background: #1a1a1a;
  padding: 2rem;
  overflow-x: visible;
`

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3rem;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
`

const LogoContainer = styled.div`
  text-align: center;
  flex: 1;
  background: ${theme.color.background.light};
  border-radius: 8px;
  padding: 2rem;
  margin: 0 1rem;
`

const AppLogo = styled.img`
  max-height: 120px;
  max-width: 400px;
  height: auto;
  width: auto;
  object-fit: contain;
`

const UserMenuContainer = styled.div`
  position: fixed;
  top: 2rem;
  right: 2rem;
  z-index: ${theme.zIndex.navigation};
  display: flex;
  align-items: center;
  justify-content: flex-end;
`

const RegionScopeNotice = styled.div`
  max-width: 800px;
  margin: 3rem auto;
  padding: 2rem;
  background: #fff5f5;
  border: 1px solid #f5c6cb;
  border-left: 6px solid #c8002b;
  border-radius: 6px;
  color: #721c24;
  font-size: 1.2rem;
  line-height: 1.5;
  text-align: center;

  h3 {
    margin: 0 0 0.5rem;
    font-size: 1.6rem;
    color: #c8002b;
  }
`

const ActionBar = styled.div`
  max-width: 1400px;
  margin: 0 auto 1.5rem;
  padding: 0 0.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  justify-content: flex-start;
`

const MainContent = styled.main`
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
  background: rgba(255, 0, 0, 0.05);
`

const UserDataContainer = styled.div`
  margin-top: 2rem;
`

const CustomAttributesSubsection = styled.div`
  margin-top: 3rem;
  opacity: 0.8;
  transform: scale(0.95);
  
  h2 {
    color: ${theme.color.text.grey};
    font-size: 2rem;
  }
`

export const portalTierLabel = (tier: unknown): string => {
  if (typeof tier === 'string' || typeof tier === 'number') return String(tier)
  if (!tier || typeof tier !== 'object' || Array.isArray(tier)) return 'N/A'

  const tierFields = tier as Record<string, unknown>
  for (const field of ['name', 'display_name', 'level', 'rank', 'id']) {
    const value = tierFields[field]
    if (typeof value === 'string' || typeof value === 'number') return String(value)
  }

  return 'N/A'
}

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  background: ${theme.color.background.light};
  border: 1px solid ${theme.color.border.light};
  border-radius: 8px;
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
  min-height: 400px;
`

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.color.border.light};
  border-top: 4px solid ${theme.color.accent.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

const LoadingText = styled.div`
  font-size: 1.4rem;
  color: ${theme.color.text.grey};
  text-align: center;
`

// Global merge status notification
const MergeStatusMessage = styled.div`
  position: fixed;
  top: 1rem;
  right: 1rem;
  background: #e3f2fd;
  color: #1565c0;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  font-size: 1.3rem;
`

const MergeStatusSpinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid #90caf9;
  border-top: 2px solid #1565c0;
  border-radius: 50%;
  animation: mergeStatusSpin 1s linear infinite;
  flex-shrink: 0;

  @keyframes mergeStatusSpin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

const AdminApp: React.FC = () => {
  const { isSuperAdmin, isAdmin, studioRegionScope } = useAuth()
  const hasSMAccess = isSuperAdmin
  // Region-scope check uses the Mariana Tek home_location id as the source of
  // truth (the SessionM custom_attributes.home_studio attribute isn't always
  // set, e.g. r.lewis@barrysmiami.com — MT carries it reliably).
  // Returns 'loading' while marianaTekData hasn't arrived yet so we don't
  // accidentally block an in-scope client during the initial load.
  const checkRegionScope = (
    mtData: any,
    scope: typeof studioRegionScope,
  ): 'no-scope' | 'loading' | 'allow' | 'block' => {
    if (!scope) return 'no-scope'
    if (!mtData) return 'loading'
    const mtHomeLocId = Number(mtData?.data?.[0]?.relationships?.home_location?.data?.id)
    if (!mtHomeLocId) return 'block'
    return (scope.locationIds as readonly number[]).includes(mtHomeLocId) ? 'allow' : 'block'
  }

  const [userData, setUserData] = useState<any>(null)
  const [refreshTimestamp, setRefreshTimestamp] = useState<number>(Date.now())
  const [ticketCount, setTicketCount] = useState<number>(0)
  const [showCreateProfile, setShowCreateProfile] = useState(false)
  const [createProfileEmail, setCreateProfileEmail] = useState('')
  const [createProfileLoading, setCreateProfileLoading] = useState(false)
  const [createProfileError, setCreateProfileError] = useState('')
  const [createProfileSuccess, setCreateProfileSuccess] = useState('')
  const [urlEmailParam, setUrlEmailParam] = useState<string | null>(null)
  const [urlMtIdParam, setUrlMtIdParam] = useState<string | null>(null)
  const [urlSmIdParam, setUrlSmIdParam] = useState<string | null>(null)
  const [isProfileDataLoading, setIsProfileDataLoading] = useState(false)
  const [marianaTekData, setMarianaTekData] = useState<any>(null)

  // For region-scoped admins, fetch MT user data eagerly so the scope check
  // doesn't hang on "Checking your region access…" — normally marianaTekData
  // is set by UserProfile inside the tabs, which we don't render until the
  // scope decision is made (chicken-and-egg).
  useEffect(() => {
    if (!studioRegionScope || !userData || marianaTekData) return
    const mtId = userData?.user?.external_id
    if (!mtId) {
      setMarianaTekData({ data: [] })
      return
    }
    let cancelled = false
    fetch(`/api/marianatek/api/users/${mtId}/?include=home_location`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return
        setMarianaTekData({ data: data?.data ? [data.data] : [], included: data?.included })
      })
      .catch(() => {
        if (cancelled) return
        setMarianaTekData({ data: [] })
      })
    return () => {
      cancelled = true
    }
  }, [studioRegionScope, userData, marianaTekData])

  const [barrysPlatformData, setBarrysPlatformData] = useState<any>(null)
  const [reservationsData, setReservationsData] = useState<any>(null)
  const [globalMergeStatus, setGlobalMergeStatus] = useState<string>('')
  const mtMergeEnabled = mtMergeReleaseEnabled()
  const isRefreshingRef = React.useRef(false)
  const lastRefreshTimeRef = React.useRef<number>(0)

  // Poll localStorage for merge operation updates so queued and uncertain
  // operations remain visible across tabs and customer searches.
  // Compute the desired value first and only call setState when it actually changed —
  // a raw setGlobalMergeStatus on every tick caused enough scheduler churn to make
  // long-running tabs (Challenge Data) look like they were continually refreshing.
  useEffect(() => {
    if (!mtMergeEnabled) return undefined

    const computeMergeStatus = (): string => {
      return mtMergeOperationStatusMessage(localStorage.getItem(MT_MERGE_OPERATION_STORAGE_KEY))
    }

    const checkMergeStatus = () => {
      const next = computeMergeStatus()
      setGlobalMergeStatus((prev) => (prev === next ? prev : next))
    }

    // Check immediately on mount
    checkMergeStatus()

    // Poll every 500ms to catch updates from MTMerge component
    const interval = setInterval(checkMergeStatus, 500)

    return () => clearInterval(interval)
  }, [mtMergeEnabled])

  // Check for URL parameters on mount (email, mt_id, sm_id)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const emailParam = urlParams.get('email')
    const mtIdParam = urlParams.get('mt_id') || urlParams.get('external_id')
    const smIdParam = urlParams.get('sm_id') || urlParams.get('sessionm_id')
    if (emailParam) setUrlEmailParam(emailParam)
    if (mtIdParam) setUrlMtIdParam(mtIdParam)
    if (smIdParam) setUrlSmIdParam(smIdParam)
  }, [])

  const handleUserFound = (data: any) => {
    setUserData(data)
    setTicketCount(0) // Reset ticket count when new user is selected
    setShowCreateProfile(false) // Hide create profile form
    setCreateProfileEmail('')
    setCreateProfileError('')
    setCreateProfileSuccess('')
    // Reset profile data when switching users
    setMarianaTekData(null)
    setBarrysPlatformData(null)
    setReservationsData(null)
    // Force full remount of all tab components by updating the refresh timestamp
    setRefreshTimestamp(Date.now())

    // Resilience for profiles where SessionM's external_id doesn't actually
    // hold the MT user id — e.g. carolinelancia@optonline.net carries her
    // Barry's Platform UUID in that slot. MT ids are always numeric, so if
    // external_id isn't a positive integer string, resolve the real MT id
    // from MT by email and patch userData so every downstream tab
    // (Reservations, MT Merge, UWB lookup, region scope, …) sees the right
    // id. The original value is preserved under _sessionm_external_id_raw
    // for the Platform IDs section.
    const rawExternalId = String(data?.user?.external_id ?? '')
    const email = data?.user?.email
    if (email && !/^\d+$/.test(rawExternalId)) {
      fetch(`/api/marianatek/api/users/?email=${encodeURIComponent(email)}`)
        .then((r) => r.json())
        .then((mt) => {
          const exact = (mt?.data || []).find(
            (u: any) =>
              (u?.attributes?.email || '').toLowerCase() === email.toLowerCase() &&
              !u?.attributes?.merged_into_id,
          )
          if (!exact?.id) return
          setUserData((prev: any) =>
            prev?.user
              ? {
                  ...prev,
                  user: {
                    ...prev.user,
                    external_id: String(exact.id),
                    _sessionm_external_id_raw: rawExternalId,
                  },
                }
              : prev,
          )
          // Tabs already mounted with the bad external_id and won't
          // refetch on prop change; reset MT/platform/reservations state
          // and bump the refresh key so every tab remounts and re-fetches
          // with the corrected MT id.
          setMarianaTekData(null)
          setBarrysPlatformData(null)
          setReservationsData(null)
          setRefreshTimestamp(Date.now())
        })
        .catch(() => {
          /* leave userData alone; tabs will surface their own errors */
        })
    }
  }

  const handleCreateProfile = (email: string) => {
    setCreateProfileEmail(email)
    setShowCreateProfile(true)
    setCreateProfileError('')
    setCreateProfileSuccess('')
  }

  const handleCreateProfileSubmit = async (formData: CreateProfileFormData) => {
    setCreateProfileLoading(true)
    setCreateProfileError('')
    setCreateProfileSuccess('')
    
    try {
      const newUser = await createUser(formData)
      setCreateProfileSuccess('User profile created successfully!')
      
      // Automatically load the new user
      setTimeout(() => {
        handleUserFound(newUser)
      }, 2000)
      
    } catch (err) {
      setCreateProfileError(err instanceof Error ? err.message : 'Failed to create user profile')
    } finally {
      setCreateProfileLoading(false)
    }
  }

  const handleCreateProfileCancel = () => {
    setShowCreateProfile(false)
    setCreateProfileEmail('')
    setCreateProfileError('')
    setCreateProfileSuccess('')
  }

  const handleUpdate = useCallback(async () => {
    const clickTime = Date.now()
    console.warn('========================================')
    console.warn('🖱️🖱️🖱️ REFRESH BUTTON CLICKED 🖱️🖱️🖱️')
    console.warn('========================================')
    console.log('🖱️ handleUpdate called at:', clickTime)

    if (!userData?.user?.id) {
      console.log('No user data, skipping refresh')
      return
    }

    const now = Date.now()
    const timeSinceLastRefresh = now - lastRefreshTimeRef.current
    const MIN_REFRESH_INTERVAL = 2000 // 2 seconds minimum between refreshes

    console.log(`⏱️ Time since last refresh: ${timeSinceLastRefresh}ms | Last refresh was at: ${lastRefreshTimeRef.current} | Currently refreshing: ${isRefreshingRef.current}`)

    // Prevent refreshes within 2 seconds of the last one
    if (timeSinceLastRefresh < MIN_REFRESH_INTERVAL) {
      console.log(`⛔ Refresh blocked - must wait ${Math.ceil((MIN_REFRESH_INTERVAL - timeSinceLastRefresh) / 1000)}s`)
      return
    }

    // Prevent concurrent refreshes
    if (isRefreshingRef.current) {
      console.log('⛔ Already refreshing, ignoring duplicate refresh request')
      return
    }

    isRefreshingRef.current = true
    lastRefreshTimeRef.current = now

    try {
      console.log('✅ Starting refresh for user:', userData.user.id, '(SessionM ID)')

      // Update refresh timestamp to force components to re-fetch data
      setRefreshTimestamp(Date.now())

      // Refresh main user data with timeout
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Refresh timeout after 30 seconds')), 30000)
      )

      console.log('🔄 Fetching updated user profile from SessionM...')
      const updatedData = await Promise.race([
        getUserBySessionMId(userData.user.id),
        timeoutPromise
      ])

      setUserData(updatedData)

      console.log('✅ Refresh completed successfully')
    } catch (err) {
      console.error('❌ Failed to refresh user data:', err)
      // Don't throw - let the UI continue to function with stale data
    } finally {
      isRefreshingRef.current = false
      console.log('🔓 Refresh lock released')
    }
  }, [userData])

  return (
    <AppContainer>
      {/* Global merge status notification - persists across tab changes and user searches */}
      {isSuperAdmin && globalMergeStatus && (
        <MergeStatusMessage>
          <MergeStatusSpinner />
          {globalMergeStatus}
        </MergeStatusMessage>
      )}

      <Header>
        <div style={{ minWidth: '200px' }}></div>
        <LogoContainer>
          <AppLogo src="/portal-logo.png" alt="Barry's Admin Portal" />
        </LogoContainer>
        <div style={{ minWidth: '200px' }}></div>
        <UserMenuContainer>
          <UserMenu />
        </UserMenuContainer>
      </Header>

      {isAdmin && (
        <ActionBar>
          {isSuperAdmin && <GiftCardAuditLog />}
          {isSuperAdmin && <AdminActivityLog />}
          {isSuperAdmin && <UserManagement />}
          <StudioChallengesButton />
          {isSuperAdmin && <BulkTagsButton />}
        </ActionBar>
      )}

      <MainContent>
        <UserSearch
          onUserFound={handleUserFound}
          onCreateProfile={isSuperAdmin ? handleCreateProfile : undefined}
          urlEmailParam={urlEmailParam}
          urlMtIdParam={urlMtIdParam}
          urlSmIdParam={urlSmIdParam}
          userData={userData}
          onRefresh={handleUpdate}
          readOnly={!isAdmin}
        />
        
        {isSuperAdmin && showCreateProfile && (
          <CreateProfileForm
            initialEmail={createProfileEmail}
            onSubmit={handleCreateProfileSubmit}
            onCancel={handleCreateProfileCancel}
            isLoading={createProfileLoading}
            error={createProfileError}
            success={createProfileSuccess}
          />
        )}
        
        {(() => {
          if (!userData || showCreateProfile) return null
          const status = checkRegionScope(marianaTekData, studioRegionScope)
          if (status === 'loading') {
            return (
              <LoadingOverlay>
                <LoadingSpinner />
                <LoadingText>Checking your region access…</LoadingText>
              </LoadingOverlay>
            )
          }
          if (status === 'block') {
            return (
              <RegionScopeNotice>
                <h3>Outside your region</h3>
                <p>
                  You're scoped to the <b>{studioRegionScope?.region}</b> region and can only
                  view clients whose home studio is in that region. This client's home studio
                  is not in your scope.
                </p>
              </RegionScopeNotice>
            )
          }
          return null
        })()}

        {userData && !showCreateProfile && (checkRegionScope(marianaTekData, studioRegionScope) === 'no-scope' || checkRegionScope(marianaTekData, studioRegionScope) === 'allow') && (
          <UserDataContainer>
            <TabContainer
              key={userData?.user?.id || 'no-user'}
              tabs={[
                {
                  id: 'profile',
                  label: 'User Profile',
                  content: isProfileDataLoading ? (
                    <LoadingOverlay>
                      <LoadingSpinner />
                      <LoadingText>Loading user profile data...</LoadingText>
                    </LoadingOverlay>
                  ) : (
                    <div>
                      <UserProfile
                        userData={userData}
                        refreshTrigger={refreshTimestamp}
                        onLoadingStateChange={setIsProfileDataLoading}
                        onExternalDataLoaded={(mtData, bpData, reservData) => {
                          setMarianaTekData(mtData)
                          setBarrysPlatformData(bpData)
                          setReservationsData(reservData)
                        }}
                        onUpdate={handleUpdate}
                      />
                      <CustomAttributesSubsection>
                        <CustomAttributes userData={userData} />
                      </CustomAttributesSubsection>
                    </div>
                  )
                },
                {
                  id: 'academy',
                  label: 'The Academy',
                  content: (
                    <AcademySection userData={userData} onUpdate={handleUpdate} refreshTimestamp={refreshTimestamp} />
                  )
                },
                {
                  id: 'offers',
                  label: 'Current Offers',
                  content: isProfileDataLoading ? (
                    <LoadingOverlay>
                      <LoadingSpinner />
                      <LoadingText>Loading user profile data...</LoadingText>
                    </LoadingOverlay>
                  ) : (
                    <OffersSection userData={userData} onUpdate={handleUpdate} />
                  )
                },
                {
                  id: 'purchases',
                  label: 'Purchases',
                  content: isProfileDataLoading ? (
                    <LoadingOverlay>
                      <LoadingSpinner />
                      <LoadingText>Loading user profile data...</LoadingText>
                    </LoadingOverlay>
                  ) : (
                    <PurchaseHistory userData={userData} onUpdate={handleUpdate} refreshTimestamp={refreshTimestamp} />
                  )
                },
                {
                  id: 'reservations',
                  label: 'Reservations',
                  content: isProfileDataLoading ? (
                    <LoadingOverlay>
                      <LoadingSpinner />
                      <LoadingText>Loading user profile data...</LoadingText>
                    </LoadingOverlay>
                  ) : (
                    <Reservations userData={userData} reservationsData={reservationsData} />
                  )
                },
                {
                  id: 'challenge-data',
                  label: 'Challenge Data',
                  content: isProfileDataLoading ? (
                    <LoadingOverlay>
                      <LoadingSpinner />
                      <LoadingText>Loading user profile data...</LoadingText>
                    </LoadingOverlay>
                  ) : (
                    <ChallengeData userData={userData} reservationsData={reservationsData} marianaTekData={marianaTekData} />
                  )
                },
                {
                  id: 'email-comms',
                  label: 'E-mail Comms',
                  content: isProfileDataLoading ? (
                    <LoadingOverlay>
                      <LoadingSpinner />
                      <LoadingText>Loading user profile data...</LoadingText>
                    </LoadingOverlay>
                  ) : (
                    <EmailComms userData={userData} />
                  )
                },
                // FWB history is available to all admitted portal roles.
                ...[{
                  id: 'fwb',
                  label: 'FWB',
                  content: isProfileDataLoading ? (
                    <LoadingOverlay>
                      <LoadingSpinner />
                      <LoadingText>Loading user profile data...</LoadingText>
                    </LoadingOverlay>
                  ) : (
                    <FWB userData={userData} />
                  )
                }],
                {
                  id: 'help-tickets',
                  label: 'Help Tickets',
                  content: isProfileDataLoading ? (
                    <LoadingOverlay>
                      <LoadingSpinner />
                      <LoadingText>Loading user profile data...</LoadingText>
                    </LoadingOverlay>
                  ) : (
                    <HelpTickets userData={userData} onTicketCountChange={setTicketCount} />
                  ),
                  badge: ticketCount
                },
                ...(isAdmin ? [{
                  id: 'fsa-receipts',
                  label: 'FSA/HSA Receipts',
                  content: isProfileDataLoading ? (
                    <LoadingOverlay>
                      <LoadingSpinner />
                      <LoadingText>Loading user profile data...</LoadingText>
                    </LoadingOverlay>
                  ) : (
                    <FSAReceipts userData={userData} />
                  )
                }] : []),
                // Only show Bulk Points tab for super admins
                ...(isSuperAdmin ? [{
                  id: 'bulk-points',
                  label: 'Bulk Points',
                  content: <BulkPoints />
                }] : []),
                // Only show SM tab for authorized users
                ...(hasSMAccess ? [{
                  id: 'sm',
                  label: 'SM',
                  content: isProfileDataLoading ? (
                    <LoadingOverlay>
                      <LoadingSpinner />
                      <LoadingText>Loading user profile data...</LoadingText>
                    </LoadingOverlay>
                  ) : (
                    <SessionMTools
                      userData={userData}
                      marianaTekData={marianaTekData}
                      barrysPlatformData={barrysPlatformData}
                    />
                  )
                }] : []),
                // Only show SM Merge tab for super admins
                ...(isSuperAdmin ? [{
                  id: 'sm-merge',
                  label: 'SM Merge',
                  content: isProfileDataLoading ? (
                    <LoadingOverlay>
                      <LoadingSpinner />
                      <LoadingText>Loading user profile data...</LoadingText>
                    </LoadingOverlay>
                  ) : (
                    <CustomerMerge userData={userData} />
                  )
                }] : []),
                // Only show MT Merge tab for super admins
                ...(isSuperAdmin && mtMergeEnabled ? [{
                  id: 'mt-merge',
                  label: 'MT Merge',
                  content: isProfileDataLoading ? (
                    <LoadingOverlay>
                      <LoadingSpinner />
                      <LoadingText>Loading user profile data...</LoadingText>
                    </LoadingOverlay>
                  ) : (
                    <MTMerge userData={userData} />
                  )
                }] : []),
                // Only show Audit Log tab for super admins
                ...(isSuperAdmin ? [{
                  id: 'audit-log',
                  label: 'Audit Log',
                  content: isProfileDataLoading ? (
                    <LoadingOverlay>
                      <LoadingSpinner />
                      <LoadingText>Loading user profile data...</LoadingText>
                    </LoadingOverlay>
                  ) : (
                    <AuditLog />
                  )
                }] : []),
              ]}
              defaultTab="profile"
            />
          </UserDataContainer>
        )}
      </MainContent>
      
    </AppContainer>
  )
}

export default AdminApp
